"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";

type ImagePosition = "left" | "right";

/** 单个服务项配置：id 对应翻译 key 前缀，sectionId 为滚动锚点，image 为可选图片路径 */
export interface ServiceConfig {
	id: string;
	/** 用于滚动锚点，与按钮点击滚动对应 */
	sectionId: string;
	/** 该服务展示的图片路径，可选；不传则不展示图片或由调用方传入默认图 */
	image: string;
	imagePosition: ImagePosition;
	rowCount: number;
	/** 可选「Learn More」外链 */
	learnMoreUrl?: string;
}

const DEFAULT_GET_QUOTE_HREF = "/upload";

const PM_SERVICES: ServiceConfig[] = [
	{
		id: "wireEdm",
		sectionId: "wire-edm",
		image: "/image/new-version/machining/precision-machining/cnc/wire-edm-service.jpg",
		imagePosition: "right",
		rowCount: 6,
	},
	{
		id: "mirrorEdm",
		sectionId: "mirror-edm",
		image: "/image/new-version/machining/precision-machining/cnc/mirror-edm-service.jpg",
		imagePosition: "left",
		rowCount: 7,
	},
	{
		id: "grinding",
		sectionId: "grinding",
		image: "/image/new-version/machining/precision-machining/cnc/grinding-service.webp",
		imagePosition: "right",
		rowCount: 5,
	},
	{
		id: "cncTurning",
		sectionId: "cnc-turning",
		image: "/image/new-version/machining/precision-machining/cnc/cnc-turning-services.webp",
		imagePosition: "left",
		rowCount: 7,
		learnMoreUrl: "/cnc-turning",
	},
	{
		id: "cncMilling",
		sectionId: "cnc-milling",
		image: "/image/new-version/machining/precision-machining/cnc/cnc-milling-service.webp",
		imagePosition: "right",
		rowCount: 6,
		learnMoreUrl: "/cnc-milling",
	},
];

export interface PrecisionMachiningServicesProps {
	/** 翻译命名空间，需包含 sectionTitle、tableCapability、tableDetails、getQuoteButton、learnMore 及各 serviceId 的 TabLabel、Subtitle、Desc、Row*Cap、Row*Det */
	translationNamespace?: string;
	/** Get Instant Quote 按钮链接，不传则用 /upload */
	getQuoteHref?: string;
	/** 布局变体：default 为卡片规格布局，split 为截图同款左右分栏文字布局 */
	layoutVariant?: "default" | "split";
	/** 服务列表配置（id、sectionId、image、imagePosition、rowCount、learnMoreUrl）；不传则使用 Precision Machining 默认 5 项 */
	services?: ServiceConfig[];
}

export default function PrecisionMachiningServices({
	translationNamespace = "precisionMachiningServices",
	getQuoteHref = DEFAULT_GET_QUOTE_HREF,
	layoutVariant = "default",
	services = PM_SERVICES,
}: PrecisionMachiningServicesProps) {
	const t = useTranslations(translationNamespace);

	const getServiceSpecs = (serviceId: string, rowCount: number) =>
		Array.from({ length: rowCount }, (_, i) => i + 1).map((rowNum) => ({
			label: t(`${serviceId}Row${rowNum}Cap`),
			value: t(`${serviceId}Row${rowNum}Det`),
		}));

	const scrollToSection = (sectionId: string) => {
		const el = document.getElementById(sectionId);
		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const renderSectionTitle = () => {
		const title = t("sectionTitle");
		const parts = title.split(" ");
		const lastWord = parts[parts.length - 1];
		const firstPart = parts.slice(0, -1).join(" ");

		return (
			<>
				<span className="block">{firstPart}</span>
				<span className="flex items-end gap-3">
					{lastWord}
					<span
						className="mb-[0.18em] h-[2px] w-12 shrink-0 bg-themePrimary400 sm:w-16 md:w-20"
						aria-hidden
					/>
				</span>
			</>
		);
	};

	const renderSplitParagraphs = (serviceId: string) => {
		const paragraphs = t(`${serviceId}Desc`)
			.split("\n\n")
			.map((paragraph) => paragraph.trim())
			.filter(Boolean);

		return paragraphs.map((paragraph) => (
			<p key={`${serviceId}-${paragraph}`}>{paragraph}</p>
		));
	};

	if (layoutVariant === "split") {
		return (
			<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
				<div className="container">
					<div className="mb-6 text-left sm:mb-8 md:mb-10 lg:mb-12">
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{renderSectionTitle()}
						</h2>
					</div>

					<div className="mb-10 flex flex-wrap justify-center gap-3 sm:mb-12">
						{services.map((svc) => (
							<button
								key={svc.id}
								type="button"
								onClick={() => scrollToSection(svc.sectionId)}
								className="rounded-lg border border-themePrimary400 px-4 py-2.5 text-sm font-medium text-themePrimary400 transition-colors hover:bg-themePrimary400 hover:text-white sm:px-4 sm:py-3 sm:text-base"
							>
								{t(`${svc.id}TabLabel`)}
							</button>
						))}
					</div>

					<div className="space-y-10 md:space-y-12 lg:space-y-14">
						{services.map((svc) => (
							<article
								key={svc.id}
								id={svc.sectionId}
								className="scroll-mt-24 pb-2 md:scroll-mt-28"
							>
								<div
									className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-12 xl:gap-16 ${
										svc.imagePosition === "left"
											? "lg:[&>*:first-child]:col-start-2 lg:[&>*:last-child]:col-start-1 lg:[&>*:last-child]:row-start-1"
											: ""
									}`}
								>
									<div className="min-w-0">
										<h3 className="mb-4 text-lg font-bold leading-tight text-black sm:text-xl md:text-2xl lg:mb-5">
											{t(`${svc.id}Subtitle`)}
										</h3>
										<div className="space-y-4 text-sm leading-relaxed text-black md:text-base">
											{renderSplitParagraphs(svc.id)}
										</div>

										<div className="mt-6 space-y-3 text-sm leading-relaxed text-black md:mt-8 md:text-base">
											{getServiceSpecs(svc.id, svc.rowCount).map((spec) => (
												<p key={`${svc.id}-${spec.label}`} className="leading-relaxed">
													<span className="font-bold text-black">{spec.label}: </span>
													<span className="whitespace-pre-line">{spec.value}</span>
												</p>
											))}
										</div>

										<div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4 md:mt-10">
											<Link
												href={getQuoteHref}
												className="inline-flex items-center justify-center rounded-md bg-themePrimary400 px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:px-6 sm:text-base"
											>
												{t("getQuoteButton")}
											</Link>
											{svc.learnMoreUrl && (
												<Link
													href={svc.learnMoreUrl}
													className="inline-flex items-center gap-2 text-sm font-semibold text-themePrimary400 transition-colors hover:opacity-80 sm:text-base"
												>
													<span>{t("learnMore")}</span>
													<svg
														className="h-4 w-4 flex-shrink-0"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
														aria-hidden
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M7 17L17 7M9 7h8v8"
														/>
													</svg>
												</Link>
											)}
										</div>
									</div>

									<div className="mx-auto w-full max-w-[520px] border border-[#e7e2d9] bg-white p-4 shadow-[0_1px_4px_rgba(15,23,42,0.06)] sm:p-5 lg:max-w-none">
										<div className="relative aspect-[1/1] w-full bg-white">
											<Image
												src={svc.image}
												alt={t(`${svc.id}Subtitle`)}
												fill
												className="object-contain p-2"
												sizes="(max-width: 1024px) 100vw, 42vw"
											/>
										</div>
									</div>
								</div>
							</article>
						))}
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 主标题 + 绿色装饰线 */}
				<div className="mb-6 text-left sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{renderSectionTitle()}
					</h2>
				</div>

				{/* 导航按钮：点击滚动到对应内容区 */}
				<div className="mb-10 flex flex-wrap justify-center gap-3 sm:mb-12">
					{services.map((svc) => (
						<button
							key={svc.id}
							type="button"
							onClick={() => scrollToSection(svc.sectionId)}
							className="rounded-lg border border-themePrimary400 px-4 py-2.5 text-sm font-medium text-themePrimary400 transition-colors hover:bg-themePrimary400 hover:text-white sm:px-4 sm:py-3 sm:text-base"
						>
							{t(`${svc.id}TabLabel`)}
						</button>
					))}
				</div>

				{/* 服务内容全部展示，每块带锚点 id */}
				{services.map((svc) => (
					<div
						key={svc.id}
						id={svc.sectionId}
						className="mb-10 scroll-mt-24 pt-12 first:border-t-0 first:pt-0 md:scroll-mt-28"
					>
						<div
							className={`flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10 xl:gap-12 ${
								svc.imagePosition === "left" ? "lg:flex-row-reverse" : ""
							}`}
						>
							{/* 文字与规格区 */}
							<div className="flex min-h-0 w-full min-w-0 flex-col lg:w-1/2">
								<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:mb-5 md:text-2xl">
									{t(`${svc.id}Subtitle`)}
								</h3>
								<div className="mb-6 space-y-4 text-sm leading-relaxed text-black md:text-base">
									{t(`${svc.id}Desc`)
										.split("\n\n")
										.map((paragraph) => (
											<p key={paragraph}>{paragraph}</p>
										))}
								</div>

								<div className="grid gap-3 sm:gap-4">
									{getServiceSpecs(svc.id, svc.rowCount).map((spec) => (
										<div
											key={`${svc.id}-${spec.label}`}
											className="rounded-2xl border border-[#d4b99a]/50 bg-[#f8f3ed] px-4 py-4 sm:px-5"
										>
											<div className="mb-2 text-sm font-semibold text-[#9a6c3b] sm:text-base">
												{spec.label}
											</div>
											<div className="whitespace-pre-line text-sm leading-relaxed text-black md:text-base">
												{spec.value}
											</div>
										</div>
									))}
								</div>
								<div className="flex flex-wrap items-center gap-4 pt-6 sm:gap-5 sm:pt-8">
									<Link
										href={getQuoteHref}
										className="group inline-flex items-center justify-center rounded bg-themePrimary400 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-6 sm:py-3 sm:text-base"
									>
										{t("getQuoteButton")}
									</Link>
									{svc.learnMoreUrl && (
										<Link
											href={svc.learnMoreUrl}
											className="inline-flex items-center gap-1.5 text-sm font-semibold text-themePrimary400 transition-colors hover:underline sm:text-base"
										>
											<span>{t("learnMore")}</span>
											<svg
												className="sm:h-4.5 sm:w-4.5 h-4 w-4 flex-shrink-0"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												aria-hidden
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
												/>
											</svg>
										</Link>
									)}
								</div>
							</div>

							{/* 图片区：适配竖图 730×1030，带边框，宽度占50% */}
							<div className="relative w-full flex-shrink-0 overflow-hidden rounded-lg border-2 border-gray-100 lg:w-1/2">
								<div className="relative aspect-[730/900] w-full">
									<Image
										src={svc.image}
										alt={t(`${svc.id}Subtitle`)}
										fill
										className="object-fill"
										sizes="(max-width: 1024px) 100vw, 45vw"
									/>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</section>
	);
}
